import type { ComponentType, FC, KeyboardEvent, ReactNode } from 'react';
import {
  Button, ClickableTile, Column, Grid, IconButton, InlineLoading, Layer, ProgressIndicator, ProgressStep,
  Stack, Tag, TextInput, Tile,
} from '@carbon/react';
import { ArrowRight, Calendar, Checkmark, Close, Folder, Locked, Music, UserMultiple } from '@carbon/icons-react';
import { IconeDaProducao, IconeDeVendas } from '../../casca/iconesDosModulos';
import { Receipt, Unlock_01 } from '@carbon/pictograms-react';

import type { DadosDoDesbloqueio } from '../../../logica/dinheiro/useDesbloqueio';
import { CarregandoApp } from '../../padroes/Carregando';
import { Vazio } from '../../padroes/Vazio';
import { ROTULOS, traduzirPasso } from '../../padroes/rotulos';
import { useDoMd } from '../../padroes/useDoMd';
import { Entrega } from '../perfil/Diagnostico/Entrega';
import { LetrasReal } from '../perfil/Diagnostico/RelatorioReal';
import { IconeDaNyta } from '../../marcas/MarcaDaNyta';
import { Sucesso } from './Sucesso';
import styles from './Desbloqueio.module.scss';

// O DESBLOQUEIO DE UM PERFIL (/artists/:id/desbloquear), em tela cheia, fora da casca: a passagem
// do diagnóstico (que já é dela) para o planejamento. O topo diz onde ela está no fluxo; embaixo,
// a etapa da vez: o relatório, a jornada e o checkout (o mesmo da /planos).
//
// A jornada é curta (04/10/2026, pedido do Lucas): as três etapas do ciclo, com o botão "Criar
// planejamento" no cartão da etapa da vez (à vista sem rolar) e as seguintes trancadas, mas
// legíveis. O código de acesso e o que o PRO inclui moram no pagamento, na coluna do formulário.

type D = DadosDoDesbloqueio;

// Os ícones do menu, por inclusão (a venda e a produção são os dois do Controle de shows).
type Icone = ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean }>;
const ICONES_DAS_INCLUSOES: Record<string, Icone> = {
  nyta: IconeDaNyta as Icone,
  musicas: Music,
  venda: IconeDeVendas as Icone,
  controle: IconeDaProducao as Icone,
  agenda: Calendar,
  equipe: UserMultiple,
  armazenamento: Folder,
};

const Diagnostico: FC<{ d: D['diagnostico']; envolver: (n: ReactNode) => ReactNode }> = ({ d, envolver }) => (
  d.dados?.relatorio ? (
    <>{envolver(<Entrega cabecalho={d.cabecalho} dados={d.dados} relatorio={d.dados.relatorio} />)}</>
  ) : (
    <Stack gap={6}>
      <h2 className={styles.titulo}>Diagnóstico indisponível</h2>
      <p className={styles.texto}>Você ainda pode começar o planejamento.</p>
      <div><Button kind='primary' size='lg' renderIcon={ArrowRight} onClick={d.continuar}>Continuar</Button></div>
    </Stack>
  )
);

type PilarDaJornada = D['jornada']['pilares'][number];

const SeloDoPilar: FC<{ p: PilarDaJornada }> = ({ p }) => {
  if (p.concluido) return <Tag type='green' size='sm'>Concluído</Tag>;
  if (p.atual) return <Tag type='blue' size='sm'>Etapa atual</Tag>;
  return <Tag type='gray' size='sm' renderIcon={Locked}>Bloqueado</Tag>;
};

const ConteudoDoPilar: FC<{ p: PilarDaJornada; acao?: ReactNode }> = ({ p, acao }) => (
  <Stack gap={4} className={styles.conteudoDoPilar}>
    <div className={styles.topoDoPilar}>
      <span className={styles.rotulo}>{`${p.numero}. ${p.rotulo}`}</span>
      <SeloDoPilar p={p} />
    </div>
    <h3 className={styles.tituloDoPilar}>
      {p.bloqueado && <Locked size={16} className={styles.cadeado} aria-hidden />}
      {p.titulo}
    </h3>
    {p.letras && <LetrasReal dimensoes={p.letras} />}
    <p className={styles.apoio}>{p.linha}</p>
    {acao && <div className={styles.acaoDoPilar}>{acao}</div>}
  </Stack>
);

const LARGURA = { sm: 4, md: 8, lg: 12, className: styles.inicio } as const;

type CodigoDaJornada = NonNullable<D['jornada']['codigo']>;

/** O código de acesso, na coluna do pagamento: quem tem um não precisa pagar. */
const CampoDoCodigo: FC<{ c: CodigoDaJornada }> = ({ c }) => (
  <Tile className={styles.bloco}>
    <Layer>
      <Stack gap={5}>
        <p className={styles.texto}>{c.texto}</p>
        <div className={styles.linhaDoCodigo}>
          <TextInput id='desbloqueio-codigo' labelText='Código de acesso' value={c.valor} maxLength={30} autoComplete='off' autoFocus={c.focar}
            onChange={(e) => c.mudar(e.target.value)} onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); c.usar(); } }}
            invalid={!!c.erro} invalidText={c.erro ?? undefined} />
          {c.validando
            ? <InlineLoading description='Validando…' iconDescription={ROTULOS.carregando} className={styles.validando} />
            : <Button kind='secondary' size='md' onClick={c.usar} disabled={!c.valor.trim()}>Usar</Button>}
        </div>
      </Stack>
    </Layer>
  </Tile>
);

/** O que o PRO inclui, embaixo do pagamento, com o ícone de cada módulo no menu: o que ela vê aqui
 *  é o que encontra depois. */
const Inclusoes: FC<{ j: D['jornada'] }> = ({ j }) => (
  <Tile className={styles.bloco}>
    <Layer>
      <Stack gap={6}>
        <h3 className={styles.tituloDoBloco}>Incluso no Maestra PRO</h3>
        <ul className={styles.inclusoes}>
          {j.inclusoes.map((x) => {
            const Icone = ICONES_DAS_INCLUSOES[x.chave] ?? Checkmark;
            return (
              <li key={x.chave} className={styles.inclusao}>
                <Icone size={20} className={styles.iconeDaInclusao} aria-hidden />
                <div>
                  <span className={styles.nomeDaInclusao}>{x.titulo}</span>
                  <span className={styles.apoio}>{x.linha}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </Stack>
    </Layer>
  </Tile>
);

const Jornada: FC<{ j: D['jornada'] }> = ({ j }) => (
  <>
    <Column {...LARGURA}>
      <div className={styles.cabeca}>
        <h2 className={styles.titulo}>{j.titulo}</h2>
        <p className={styles.texto}>{j.apoio}</p>
      </div>
    </Column>

    {/* O ciclo do produto, na ordem. O feito abre (o painel do artista); o da vez leva o botão; o
        que vem depois fica à vista, trancado. */}
    {j.pilares.map((p, i) => {
      const classes = [styles.pilar, p.atual ? styles.pilarAtual : '', p.bloqueado ? styles.pilarBloqueado : ''].filter(Boolean).join(' ');
      return (
        <Column key={p.chave} sm={4} md={8} lg={4} className={`${styles.celula} ${i === 0 ? styles.inicio : ''}`}>
          {p.abrir
            ? <ClickableTile className={`${classes} ${styles.pilarQueAbre}`} onClick={p.abrir} renderIcon={ArrowRight} aria-label={`${p.titulo}: ${p.cta}`}><ConteudoDoPilar p={p} /></ClickableTile>
            : (
              <Tile className={classes}>
                <ConteudoDoPilar p={p}
                  acao={p.atual ? <Button kind='primary' size='md' renderIcon={ArrowRight} onClick={j.desbloquear}>{j.acao}</Button> : undefined} />
              </Tile>
            )}
        </Column>
      );
    })}
  </>
);

export const Desbloqueio: FC<{
  d: D;
  /** O checkout ligado (o mesmo da /planos), montado pelo contêiner com o rodapé desta tela (o
   *  código de acesso e o que o PRO inclui), que vai na coluna do pagamento, embaixo dele. */
  checkout: (rodape: ReactNode) => ReactNode;
  /** O TCLE em volta do relatório (desligado por feature flag enquanto não houver parecer). */
  envolverDiagnostico?: (n: ReactNode) => ReactNode;
}> = ({ d, checkout, envolverDiagnostico = (n) => n }) => {
  if (d.estado === 'carregando') return <CarregandoApp />;
  if (d.estado === 'done') return <Sucesso pictograma={Unlock_01} {...d.sucesso} />;
  return <Fluxo d={d} checkout={checkout} envolverDiagnostico={envolverDiagnostico} />;
};

const Fluxo: FC<{ d: D; checkout: (rodape: ReactNode) => ReactNode; envolverDiagnostico: (n: ReactNode) => ReactNode }> = ({ d, checkout, envolverDiagnostico }) => {
  const largo = useDoMd();
  return (
    <div className={styles.tela} data-ui-carbon=''>
      <header className={styles.topo}>
        <IconButton kind='ghost' size='lg' label='Sair para seus perfis' align='bottom-start' onClick={d.sair}><Close /></IconButton>
        <h1 className={styles.tituloDoTopo}>{d.nome ? `Planejamento de ${d.nome}` : 'Planejamento estratégico'}</h1>
      </header>
      <main className={styles.principal}>
        <Grid className={styles.grade}>
          {/* As etapas: a já feita é o caminho de volta (o jeito do Carbon num fluxo de passos). No
              celular, na vertical, para caberem. */}
          <Column {...LARGURA}>
            <ProgressIndicator currentIndex={d.etapa} vertical={!largo} spaceEqually={largo}
              onChange={(i: number) => d.etapas[i]?.voltar?.()}>
              {d.etapas.map((e, i) => (
                <ProgressStep key={e.rotulo} label={e.rotulo} translateWithId={traduzirPasso} complete={i < d.etapa} disabled={i > d.etapa || (i < d.etapa && !e.voltar)} />
              ))}
            </ProgressIndicator>
          </Column>
          {d.estado === 'diagnostico' && <Column sm={4} md={8} lg={16}><Diagnostico d={d.diagnostico} envolver={envolverDiagnostico} /></Column>}
          {d.estado === 'jornada' && <Jornada j={d.jornada} />}
          {/* Fora do plano grátis (pagamento pendente, perfil fora da cota, atraso), o pagamento não
              abre um checkout novo: a situação e o caminho dela (o mesmo da /planos). */}
          {d.estado === 'checkout' && d.checkout.situacao && (
            <Column {...LARGURA}>
              <Vazio
                pictograma={Receipt}
                titulo={d.checkout.situacao.titulo}
                texto={d.checkout.situacao.texto}
                acao={(
                  <>
                    <Button kind='primary' size='md' renderIcon={ArrowRight} onClick={d.checkout.situacao.ir}>{d.checkout.situacao.rotulo}</Button>
                    <Button kind='ghost' size='md' onClick={d.checkout.situacao.outra.ir}>{d.checkout.situacao.outra.rotulo}</Button>
                  </>
                )}
              />
              {d.checkout.situacao.comCodigo && d.jornada.codigo && <CampoDoCodigo c={d.jornada.codigo} />}
            </Column>
          )}
          {d.estado === 'checkout' && !d.checkout.situacao && (
            <>
              <Column {...LARGURA}>
                <div className={styles.cabeca}>
                  <h2 className={styles.titulo}>{d.checkout.titulo}</h2>
                  <p className={styles.texto}>{d.checkout.apoio}</p>
                </div>
              </Column>
              <Column sm={4} md={8} lg={16}>
                {checkout(
                  <>
                    <Inclusoes j={d.jornada} />
                    {d.jornada.codigo && <CampoDoCodigo c={d.jornada.codigo} />}
                  </>,
                )}
              </Column>
            </>
          )}
        </Grid>
      </main>
    </div>
  );
};
